import React, { ReactNode, useEffect } from 'react';
import Link from 'next/link';
import { NextSeo, ArticleJsonLd } from 'next-seo';
import { Box, Flex, Text, Link as ChakraLink, Divider, useMediaQuery } from '@chakra-ui/react';

import useTranslation from 'next-translate/useTranslation';
import dynamic from 'next/dynamic';
import { snakeCase } from 'lodash';
import PostCardNoImg from '@/components/PostCardNoImg';
// import SubscribeBlogBlock from '@/components/SubscribeBlogBlock';
import HeaderMenu from '@/components/HeaderMenu';
import Footer from '@/components/Footer';
// import SubscribeBlock from '@/components/SubscribeBlock';
import ShareButtons from '@/components/ShareButtons';
import { MarkdownRender } from '@/components/MarkdownRender';
import AuthorCard from '@/components/AuthorCard';
import PostBanner from '@/components/PostBanner';
import TableOfContent from '@/components/TableOfContent';
import AdLocasty from '@/components/AdLocasty';
import AuthorCardHorizontal from '@/components/AuthorCardHorizontal';
import TrendingCarouselPostPage from '@/components/TrendingCarouselPostPage';
import YouMayAlsoLikeContainer from '@/components/YouMayAlsoLikeContainer';
import { handleGTMClick } from '@/helpers/handleGTMClick';

export default function PostPage({
  post,
  trending,
  cookies,
  header,
  navbar,
  prevArticle,
  nextArticle,
  authorArticlesTotal,
  // homepage,
  footer,
}: any) {
  const [isMobileScreen] = useMediaQuery('(max-width: 984px)');

  const { t } = useTranslation('common');

  const Code = dynamic(() => import('@/components/Code'), {
    ssr: false,
  });

  const Comments = dynamic(() => import('@/components/Comments'), {
    ssr: false,
  });

  const scrollToHeaders = () => {
    const ArticleWrapper = document.querySelector('article');
    const SecodLevelTitles = ArticleWrapper?.querySelectorAll('h2');

    SecodLevelTitles?.forEach((item) => {
      item.setAttribute('id', item.innerText.replace(/\s/g, ''));
    });
  };

  useEffect(() => {
    scrollToHeaders();
  }, [post]);

  const renderPostSubtitleTopPickPosts = (): ReactNode => {
    let count: number = 0;

    const lines = post.data[0].attributes.content.split('\n');
    const elements: JSX.Element[] = [];
    let currentCodeLanguage: string | null = null;

    for (let index = 0; index < lines.length; index++) {
      const line = lines[index];

      if (line.trim().startsWith('## ')) {
        count += 1;

        if (count % 2 === 1 && count !== 1) {
          elements.push(
            <React.Fragment key={index}>
              <TrendingCarouselPostPage data={trending.data} />
              <MarkdownRender>{line}</MarkdownRender>
            </React.Fragment>,
          );
        } else {
          elements.push(<MarkdownRender key={index}>{line}</MarkdownRender>);
        }
      } else if (line.trim().startsWith('```')) {
        index++;
        const langMatch = line.match(/```(.*)/);

        if (langMatch) {
          /* eslint-disable */
          currentCodeLanguage = langMatch[1];
        }

        const codeLines: string[] = [];

        while (index < lines.length && !lines[index].trim().startsWith('```')) {
          codeLines.push(lines[index]);
          index++;
        }

        const code = codeLines.join('\n');

        elements.push(
          <Box key={index} mt="32px" mb="32px">
            <Code currentCodeLanguage={currentCodeLanguage} code={code} />
          </Box>,
        );

        currentCodeLanguage = null;
      } else {
        elements.push(<MarkdownRender key={index}>{line}</MarkdownRender>);
      }
    }

    return elements;
  };

  return (
    <>
      <HeaderMenu cookies={cookies.data} menu={header[0].items} submenu={navbar.data?.attributes.menu_header_list} />
      <NextSeo
        title={post.data[0].attributes.metaTitle}
        description={post.data[0].attributes.metaDescription}
        canonical={post.data[0].attributes.canonicalURL && post.data[0].attributes.canonicalURL}
        openGraph={{
          title: post.data[0].attributes.metaTitle,
          description: post.data[0].attributes.metaDescription,
          url: `${process.env.NEXT_PUBLIC_SITE_URL}/${post.data[0].attributes.url}`,
          type: 'article',
          article: {
            publishedTime: post.data[0].attributes.updatedAt,
            authors: [
              post.data[0].attributes.author.data &&
                `${process.env.NEXT_PUBLIC_SITE_URL}/perfil/${post.data[0].attributes.author.data.attributes.url}`,
            ],
          },
          images: [
            {
              url: post.data[0].attributes.mainImage.data.attributes.url,
              alt: post.data[0].attributes.metaTitle,
            },
          ],
        }}
      />

      <ArticleJsonLd
        url={`${process.env.NEXT_PUBLIC_SITE_URL}/${post.data[0].attributes.url}`}
        title={post.data[0].attributes.metaTitle}
        images={[post.data[0].attributes.mainImage.data.attributes.url]}
        datePublished={post.data[0].attributes.updatedAt}
        authorName={[
          {
            name: post.data[0].attributes.author.data && post.data[0].attributes.author.data.attributes.name,
            url:
              post.data[0].attributes.author.data &&
              `${process.env.NEXT_PUBLIC_SITE_URL}/perfil/${post.data[0].attributes.author.data.attributes.url}`,
          },
        ]}
        description={post.data[0].attributes.metaDescription}
        isAccessibleForFree
      />
      <PostBanner data={post.data[0].attributes} />
      <Box
        maxWidth="1216px"
        margin="0 auto"
        width="90%"
        mb="32px"
        as="header"
        pt={{ base: '60px', lg: '80px' }}
        pb={{ base: '60px', lg: '80px' }}
      >
        <Flex columnGap="64px" alignItems="flex-start" flexDir={{ base: 'column', lg: 'row' }}>
          <Box
            bgColor="#fff"
            as="article"
            maxWidth="100%"
            overflow="hidden"
            boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);"
            borderRadius="8px"
          >
            <Box className="article" padding="32px 32px 0 32px">
              {post.data[0].attributes.content && renderPostSubtitleTopPickPosts()}
            </Box>
            <Divider mt="32px" width="calc(100% - 64px)" display="block" bgColor="#CBD5E0" margin="0 auto" />
            <Flex
              mt="40px"
              mb="40px"
              padding="0 32px 0 32px"
              justifyContent="space-between"
              columnGap="32px"
              rowGap="20px"
              flexDirection={{ base: 'column', md: 'row' }}
            >
              <Flex columnGap="4px" alignItems="center" flexWrap="wrap" rowGap="20px">
                {post.data[0].attributes.tags.data.length > 0 && (
                  <Text marginRight="8px" fontSize="14px" fontWeight="600" color="gray.500">
                    Tags
                  </Text>
                )}
                {post.data[0].attributes.tags.data &&
                  post.data[0].attributes.tags.data.map((item: any, i: number) => (
                    <ChakraLink
                      id={`blog_tag_${snakeCase(item.attributes.url)}`}
                      onClick={handleGTMClick}
                      onAuxClick={handleGTMClick}
                      key={i}
                      as={Link}
                      padding="8px 12px"
                      borderRadius="8px"
                      bgColor="gray.100"
                      _hover={{ bgColor: 'blue.100' }}
                      color="gray.500"
                      fontWeight="500"
                      fontSize="12px"
                      href={`tag/${item.attributes.url}`}
                      w="auto"
                    >
                      #{item.attributes.tag}
                    </ChakraLink>
                  ))}
              </Flex>
              <ShareButtons url={post.data[0].attributes.url} />
            </Flex>
            <Comments id={post.data[0].id} />
            <Box padding="5px">
              <Flex
                bgColor="gray.200"
                columnGap="32px"
                rowGap="32px"
                borderBottomLeftRadius="8px"
                borderBottomRightRadius="8px"
                padding="27px 27px 43px 27px"
                justifyContent="space-between"
              >
                <Box flex="1 2 100%">
                  <Text color="gray.500" fontSize={{ base: '13', md: '16' }} lineHeight="24px" fontWeight="400px">
                    {t('article.previousArticle')}
                  </Text>
                  <ChakraLink
                    id={`blog_post_${snakeCase(prevArticle.data[0]?.attributes.url)}`}
                    onClick={handleGTMClick}
                    onAuxClick={handleGTMClick}
                    as={Link}
                    _hover={{ textDecor: 'none' }}
                    color="gray.600"
                    fontWeight="600"
                    fontSize={{ base: '13', md: '16' }}
                    marginTop="12px"
                    display="block"
                    href={`${prevArticle.data[0]?.attributes.url}`}
                  >
                    {prevArticle.data[0]?.attributes.title}
                  </ChakraLink>
                </Box>
                <Box textAlign="right" flex="1 2 100%">
                  <Text color="gray.500" fontSize={{ base: '13', md: '16' }} lineHeight="24px" fontWeight="400px">
                    {t('article.nextArticle')}
                  </Text>
                  <ChakraLink
                    id={`blog_post_${snakeCase(prevArticle.data[0]?.attributes.url)}`}
                    onClick={handleGTMClick}
                    onAuxClick={handleGTMClick}
                    as={Link}
                    _hover={{ textDecor: 'none' }}
                    color="gray.600"
                    fontWeight="600"
                    fontSize={{ base: '13', md: '16' }}
                    marginTop="12px"
                    display="block"
                    href={`${nextArticle.data[0]?.attributes.url}`}
                  >
                    {nextArticle.data[0]?.attributes.title}
                  </ChakraLink>
                </Box>
              </Flex>
            </Box>
          </Box>
          <Box
            minWidth={{ base: '100%', md: '360px', lg: '384px' }}
            as="aside"
            display="flex"
            flexDir="column"
            rowGap="64px"
            mt={{ base: '40px', lg: '0' }}
          >
            {post.data[0].attributes.author.data && (
              <AuthorCard authorArticlesTotal={authorArticlesTotal} post={post} />
            )}
            {Array.from(post.data[0].attributes.content.matchAll(/^## (.*)$/gm)).length > 0 && (
              <TableOfContent post={post} />
            )}
            {!isMobileScreen && <AdLocasty />}
            {/* <SubscribeBlogBlock /> */}
            {post.data[0].attributes.relatedArticles.data.length > 0 && (
              <Box>
                <Text fontWeight="700" fontSize="18px" mb="12px">
                  {t('article.relatedPost')}
                </Text>
                <Flex width="100%" flexDirection="column" rowGap="12px">
                  {post.data[0].attributes.relatedArticles.data.map((item: any, i: number) => (
                    <PostCardNoImg key={i} item={item} hasDate />
                  ))}
                </Flex>
              </Box>
            )}
          </Box>
        </Flex>
      </Box>
      <AuthorCardHorizontal post={post} />
      {post.data[0].attributes.relatedArticles.data && post.data[0].attributes.relatedArticles.data > 0 && (
        <YouMayAlsoLikeContainer data={post.data[0].attributes.relatedArticles.data} />
      )}

      {/* <SubscribeBlock /> */}
      <Footer menu={footer[0].items} />
    </>
  );
}
